import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Sparkles, Save, Send, Loader, FileText, AlertCircle, CheckCircle2 } from "lucide-react";

export default function LyricsReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [lyrics, setLyrics] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      setLyrics(data.lyrics_draft || "");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true); setError("");
    try {
      const { data } = await api.post(`/projects/${id}/generate-lyrics`);
      setLyrics(data.lyrics);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put(`/projects/${id}/lyrics`, { lyrics });
      setSavedAt(new Date());
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!window.confirm("Submit these lyrics for music production? You won't be able to edit them after submission.")) return;
    setSubmitting(true); setError("");
    try {
      await api.put(`/projects/${id}/lyrics`, { lyrics });
      await api.post(`/projects/${id}/submit-lyrics`);
      nav(`/dashboard/projects/${id}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setSubmitting(false);
    }
  };

  if (!project) return <div className="container-x py-20 text-white/60">Loading…</div>;

  // After submission, redirect to project detail view
  if (project.status !== "brief_draft" && project.status !== "lyrics_review") {
    return (
      <div className="container-x py-20 max-w-2xl text-center">
        <CheckCircle2 className="text-gold mx-auto mb-4" size={48} />
        <h2 className="font-heading text-2xl mb-2">Lyrics submitted</h2>
        <p className="text-white/65 mb-6">Your lyrics are now in music production. We&apos;ll notify you when songs are ready to review.</p>
        <button onClick={() => nav(`/dashboard/projects/${id}`)} className="btn-gold">Go to Project</button>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-[calc(100vh-80px)]">
      <div className="container-x py-10 max-w-4xl">
        <div className="eyebrow mb-2">AI Lyric Generation</div>
        <h1 className="font-heading text-3xl md:text-4xl mb-3">Review & Edit Your Lyrics</h1>
        <p className="text-white/55 mb-8 text-sm">
          Generate AI-powered lyrics from your business brief, then refine the text until it&apos;s perfect.
          Only after you submit final lyrics will they go to music production.
        </p>

        {/* Step indicator */}
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          <StepBadge label="1. Generate" active={!lyrics} done={!!lyrics} />
          <StepBadge label="2. Review & Edit" active={!!lyrics && project.status === "lyrics_review"} done={false} />
          <StepBadge label="3. Submit Final" active={false} done={false} />
        </div>

        {!project.lyrics_generated_at && !lyrics ? (
          <div className="bg-ink-900 border border-ink-700 p-10 text-center">
            <Sparkles className="text-gold mx-auto mb-4" size={42} />
            <h3 className="font-heading text-2xl mb-2">Ready to generate</h3>
            <p className="text-white/65 mb-6 max-w-md mx-auto">
              We&apos;ll use everything from your brief to craft original lyrics tailored to your brand.
              Generation usually takes 10–20 seconds.
            </p>
            <button onClick={generate} disabled={generating} className="btn-gold" data-testid="lyrics-generate">
              {generating ? <><Loader className="animate-spin" size={16} /> Generating…</> : <><Sparkles size={16} /> Generate Lyrics</>}
            </button>
          </div>
        ) : (
          <>
            <div className="bg-ink-900 border border-ink-700 p-5 mb-3">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 text-gold text-sm font-semibold">
                  <FileText size={16} /> Lyrics Draft (editable)
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={generate} disabled={generating} className="text-xs text-white/55 hover:text-gold uppercase tracking-widest" data-testid="lyrics-regenerate">
                    {generating ? "Regenerating…" : "Regenerate"}
                  </button>
                </div>
              </div>
              <textarea
                rows={22}
                className="input font-mono text-sm resize-y"
                value={lyrics}
                onChange={(e) => setLyrics(e.target.value)}
                data-testid="lyrics-editor"
              />
              {savedAt && <div className="text-white/40 text-xs mt-2">Saved {savedAt.toLocaleTimeString()}</div>}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm mb-3" data-testid="lyrics-error">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={save} disabled={saving || !lyrics} className="btn-outline" data-testid="lyrics-save">
                <Save size={14} /> {saving ? "Saving…" : "Save Draft"}
              </button>
              <button onClick={submit} disabled={submitting || !lyrics} className="btn-gold" data-testid="lyrics-submit">
                <Send size={14} /> {submitting ? "Submitting…" : "Submit Final Lyrics for Music Production"}
              </button>
            </div>
            <p className="text-white/45 text-xs mt-4 max-w-2xl">
              After submission, Perry receives an alert and begins music production. You&apos;ll be able to preview 3 song versions
              in your dashboard, select the ones you love, and check out from your shopping cart.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function StepBadge({ label, active, done }) {
  let className = "border-ink-700 text-white/40";
  if (done) className = "border-green-500/40 text-green-400 bg-green-400/5";
  else if (active) className = "border-gold text-gold bg-gold/5";
  return (
    <div className={`border px-4 py-3 text-[11px] tracking-[0.2em] uppercase font-semibold ${className}`}>
      {label}
    </div>
  );
}
